import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  nodeDepth,
  treeCompletion,
  type ConversationTree,
  type TreeProgress,
  type TreeSystem,
} from '@astrally/shared';
import { beatLabel, currentStep } from './suggestions';

/**
 * One conversation path, as a card.
 *
 * Four of these sit in the AI tab. Each is a different way into the same
 * chart, and each carries its own tradition colour: Jyotish runs warm, Western
 * runs cool, and the two mode-neutral paths inherit the app accent. That is
 * the whole point of `data-mode` — a user can tell which tradition is speaking
 * without reading a word.
 *
 * The card shows how DEEP the user has gone rather than how many taps they
 * have made, because depth is what unlocks: a beat three levels in has three
 * beats of the user's own answers sitting behind it, and reveals more of the
 * chart accordingly.
 */

export type TreeCardTone = TreeSystem;

/** The label the mode chip carries. Named for a reader, not for the codebase. */
const SYSTEM_LABEL: Record<TreeSystem, string> = {
  vedic: 'Jyotish · Indian',
  western: 'Western',
  social: 'You & other people',
  self: 'You, on your own',
};

/** Only the two traditions get a palette; the other two stay on the accent. */
export function modeAttr(system: TreeSystem): 'vedic' | 'western' | undefined {
  return system === 'vedic' || system === 'western' ? system : undefined;
}

export interface TreeCardProps {
  tree: ConversationTree;
  /** Undefined for a path that has never been opened. */
  progress?: TreeProgress;
  /** True while this path is the one expanded below the picker. */
  active: boolean;
  onOpen: () => void;
}

export function TreeCard({ tree, progress, active, onOpen }: TreeCardProps) {
  const maxDepth = useMemo(
    () => tree.nodes.reduce((deepest, n) => Math.max(deepest, nodeDepth(tree, n.id)), 0),
    [tree],
  );
  const done = progress?.completedNodeIds.length ?? 0;
  const depth = progress?.depth ?? 0;
  const fraction = progress ? treeCompletion(tree, progress) : 0;
  const next = currentStep(tree, progress);
  const finished = progress !== undefined && next === null;

  return (
    <button
      type="button"
      data-mode={modeAttr(tree.system)}
      onClick={onOpen}
      aria-expanded={active}
      className={`card ${active ? 'card--strong' : ''}`}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        color: 'var(--text)',
        borderColor: active ? 'var(--mode-line)' : undefined,
      }}
    >
      <div className="spread" style={{ alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span className="mode-chip">{SYSTEM_LABEL[tree.system]}</span>
          <div className="h3" style={{ marginTop: 8 }}>
            {tree.title}
          </div>
          <div className="tiny muted" style={{ marginTop: 4, lineHeight: 1.5 }}>
            {tree.blurb}
          </div>
        </div>
        <DepthRing fraction={fraction} depth={depth} maxDepth={maxDepth} />
      </div>

      <div className="mode-rule" style={{ margin: '12px 0 10px' }} />

      <div className="tiny" style={{ color: 'var(--mode-ink)' }}>
        {finished
          ? `Walked all the way through · ${done} of ${tree.nodes.length} beats`
          : next
            ? `${done === 0 ? 'Starts with' : 'Next'}: ${beatLabel(next)}`
            : 'Ready when you are'}
      </div>
    </button>
  );
}

/**
 * Progress ring. The number inside is DEPTH, not percent: "3 / 5" means three
 * levels of prerequisite deep, which is the thing that actually unlocks more
 * of the chart. The arc is completion, so a wide shallow walk and a narrow
 * deep one look different at a glance.
 */
function DepthRing({
  fraction,
  depth,
  maxDepth,
}: {
  fraction: number;
  depth: number;
  maxDepth: number;
}) {
  const size = 46;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, fraction));

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden focusable="false">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--border)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--mode)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${circumference * clamped} ${circumference}`}
          // Start the arc at 12 o'clock instead of 3.
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.7rem',
          fontWeight: 700,
          color: 'var(--mode-ink)',
        }}
      >
        {depth}/{maxDepth}
      </div>
      <span className="tiny" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        depth {depth} of {maxDepth}
      </span>
    </div>
  );
}

/**
 * What stands in for the social path while connections are off.
 *
 * The path itself is not rendered — no beats, no progress, no recommendation
 * machinery, nothing computed. Astrally is private by default and the opt-in
 * is the gate on the whole layer, not a filter over a UI that is quietly
 * running underneath. What is left is an honest explanation and a way in,
 * because a feature the user cannot find is not a private feature, it is a
 * missing one.
 */
export function LockedSocialCard({ tree }: { tree: ConversationTree }) {
  return (
    <div className="card">
      <span className="mode-chip">{SYSTEM_LABEL.social}</span>
      <div className="h3" style={{ marginTop: 8 }}>
        {tree.title}
      </div>
      <div className="tiny muted" style={{ marginTop: 4, lineHeight: 1.5 }}>
        {tree.blurb}
      </div>
      <div className="banner banner--info small" style={{ marginTop: 12 }}>
        Connections are off, so this path is switched off with them. Nothing about you is visible to
        anyone and no one is being matched with you.
      </div>
      <Link className="chip chip--on" to="/social/enable" style={{ marginTop: 12 }}>
        See what turning it on would do →
      </Link>
    </div>
  );
}
